import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import ButtonStyled from "../../../components/common/ButtonStyled";
import PrimaryButton from "../../../components/PrimaryButton";
import InputField from "../../../components/NewInput";
import DatePicker from "../../../components/DatePicker";
import PremiumDateRangePicker from "../../../components/PremiumDateRangePicker";

type Props = {
  onClose: () => void;
  refetch:()=>void;
  setFilters:any
};

export default function FilterSidebar({ onClose, setFilters,refetch }: Props) {
  const [activeTab, setActiveTab] = useState("All");

  // ✅ All filter fields in a single state
  const [rawFilters, setRawFilters] = useState({
    awbNumber: "",
    seller: "",
    warehouseName: "",
    paymentType: "",
    codAmount: "",
    sameDate: "",
    entryDateFrom: "",
    entryDateTo: "",
    closeDateFrom: "",
    closeDateTo: "",
    entryDate: "",
    closeDate: "",
    senderCityName: "",
    receiverCityName: "",
    city: "",
    serviceType: "",
    platform: "",
    shippingCompany: "",
    shippingAwbNumber: "",
  });

  const tabs = ["All", "Details", "Date", "Location", "Order"];

  // ✅ Handle input changes dynamically
  const handleChange = (key: string, value: string) => {
    setRawFilters((prev:any) => ({ ...prev, [key]: value }));
  };

  // ✅ On apply rawFilters — format the data as array
  const handleApplyFilters = () => {
    const formattedFilters = Object.entries(rawFilters)
      .filter(([_, value]) => value && String(value).trim() !== "")
      .map(([key, value]) => ({ [key]: value }));

      setFilters(formattedFilters)
      refetch();

    // console.log("Final Filters:", formattedFilters);

    // You can send formattedFilters to API or parent component here
    onClose();
  };

  return (
    <div className="relative border rounded-lg border-gray-200 w-full flex flex-col justify-start overflow-hidden bg-transparent">
      {/* Header */}
      <div>
        <div className="flex justify-between items-center px-6 py-5 border-b border-gray-200 bg-transparent sticky top-0 z-10 shrink-0">
          <h2 className="text-base font-semibold text-[#212121]">Filter Settings</h2>
          <div className="flex items-center gap-2">
            <ButtonStyled
              onClick={handleApplyFilters}
              variant="solid"
              value="Apply Filters"
              className="!h-8 !px-3.5 !text-xs"
            />
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
            >
              <IconX size={18} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-4 py-3 overflow-x-auto sticky top-12 z-50 bg-transparent shrink-0">
          {tabs.map((tab) => (
            <ButtonStyled
              key={tab}
              onClick={() => setActiveTab(tab)}
              variant={activeTab === tab ? "solid" : "outline"}
              value={tab}
              className="flex-1 !h-8 !px-3.5 !text-xs text-center justify-center"
            />
          ))}
        </div>

        {/* Filter Form */}
        <div className={`px-4 space-y-2 pb-6 transition-all ${activeTab === "Date" ? "min-h-[420px]" : ""}`}>
          {(activeTab === "All" || activeTab === "Details") && (
            <div className="space-y-2">
              <h2 className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">DETAILS</h2>
              <div className="space-y-3">
                <InputField
                  title="AWB No. (Optional)"
                  name="awbNumber"
                  placeholder="Enter AWB, Reference Number, or Mobile Number (comma-separated)"
                  value={rawFilters.awbNumber}
                  onChange={(e) => handleChange("awbNumber", e.target.value)}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputField
                    title="Seller"
                    name="seller"
                    placeholder="Amazon"
                    value={rawFilters.seller}
                    onChange={(e) => handleChange("seller", e.target.value)}
                  />
                  <InputField
                    title="Warehouse"
                    name="warehouseName"
                    placeholder="Jaipur"
                    value={rawFilters.warehouseName}
                    onChange={(e) => handleChange("warehouseName", e.target.value)}
                  />
                  <InputField
                    title="Payment Mode"
                    name="paymentType"
                    placeholder="COD/ CC"
                    value={rawFilters.paymentType}
                    onChange={(e) => handleChange("paymentType", e.target.value)}
                  />
                  <InputField
                    title="COD Amount"
                    name="codAmount"
                    placeholder="0.5 SAR"
                    value={rawFilters.codAmount}
                    onChange={(e) => handleChange("codAmount", e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {(activeTab === "All" || activeTab === "Date") && (
            <div className="space-y-2 relative z-30">
              <h2 className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">DATE SETTINGS</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 relative z-30">
                <DatePicker
                  title="Same Date"
                  dateValue={rawFilters.sameDate || ""}
                  onChangeDate={(dateStr) => handleChange("sameDate", dateStr)}
                />
                <PremiumDateRangePicker
                  title="Entry & Close Date Range"
                  startDate={rawFilters.entryDateFrom || ""}
                  endDate={rawFilters.entryDateTo || ""}
                  align="right"
                  onChangeRange={({ startDate, endDate }) => {
                    handleChange("entryDateFrom", startDate);
                    handleChange("entryDateTo", endDate);
                  }}
                />
              </div>
            </div>
          )}

          {(activeTab === "All" || activeTab === "Location") && (
            <div className="space-y-2">
              <h2 className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">LOCATION SETTINGS</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InputField
                  title="Origin"
                  name="senderCityName"
                  placeholder="Riyadh"
                  value={rawFilters.senderCityName}
                  onChange={(e) => handleChange("senderCityName", e.target.value)}
                />
                <InputField
                  title="Destination"
                  name="receiverCityName"
                  placeholder="Jeddah"
                  value={rawFilters.receiverCityName}
                  onChange={(e) => handleChange("receiverCityName", e.target.value)}
                />
                <InputField
                  title="City"
                  name="city"
                  placeholder="Mecca"
                  value={rawFilters.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                />
              </div>
            </div>
          )}

          {(activeTab === "All" || activeTab === "Order") && (
            <div className="space-y-2">
              <h2 className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">ORDER INFO</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InputField
                  title="Order Type"
                  name="serviceType"
                  placeholder="Standard / Express"
                  value={rawFilters.serviceType}
                  onChange={(e) => handleChange("serviceType", e.target.value)}
                />
                <InputField
                  title="Platform"
                  name="platform"
                  placeholder="Amazon / Noon"
                  value={rawFilters.platform}
                  onChange={(e) => handleChange("platform", e.target.value)}
                />
                <InputField
                  title="Shipping Company"
                  name="shippingCompany"
                  placeholder="SMSA / Aramex"
                  value={rawFilters.shippingCompany}
                  onChange={(e) => handleChange("shippingCompany", e.target.value)}
                />
                <InputField
                  title="Shipping AWB"
                  name="shippingAwbNumber"
                  placeholder="AWB-XXXXXX"
                  value={rawFilters.shippingAwbNumber}
                  onChange={(e) => handleChange("shippingAwbNumber", e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

